import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppTheme } from '../../core/models';
import { ApiService } from '../../core/services/api.service';
import { DateFormatService } from '../../core/services/date-format.service';
import { AuthService } from '../../core/services/auth.service';
import { CenterService } from '../../core/services/center.service';
import { PageTitleService, PageTitleState } from '../../core/services/page-title.service';
import { QuickAccessService } from '../../core/services/quick-access.service';
import { ThemeService } from '../../core/services/theme.service';

@Component({
  selector: 'app-default-header',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './default-header.component.html',
  styleUrl: './default-header.component.scss',
})
export class DefaultHeaderComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();

  currentUser: any;
  clientName   = '';
  profilePic: string | null = null;
  isUserMenuOpen   = false;
  isCenterMenuOpen = false;

  selectedCenter: any   = null;
  managerCenters: any[] = [];

  pageTitle: PageTitleState = { title: 'BookBridge', subtitle: 'Library Management System' };

  private sub!: Subscription;
  private titleSub!: Subscription;
  private picSub!: Subscription;

  // Change password modal
  showCpModal = false;
  cpCurrent = '';
  cpNew = '';
  cpConfirm = '';
  showCpCurrent = false;
  showCpNew = false;
  showCpConfirm = false;
  cpSaving = false;
  cpError = '';
  cpSuccess = '';

  get cpHas8(): boolean       { return this.cpNew.length >= 8; }
  get cpHasUpper(): boolean   { return /[A-Z]/.test(this.cpNew); }
  get cpHasLower(): boolean   { return /[a-z]/.test(this.cpNew); }
  get cpHasNumber(): boolean  { return /[0-9]/.test(this.cpNew); }
  get cpHasSpecial(): boolean { return /[@$!%*?&#^()_+\-=]/.test(this.cpNew); }
  get cpIsValid(): boolean {
    return !!this.cpCurrent && this.cpHas8 && this.cpHasUpper && this.cpHasLower &&
           this.cpHasNumber && this.cpHasSpecial && this.cpNew === this.cpConfirm;
  }

  openCpModal(): void {
    this.isUserMenuOpen = false;
    this.cpCurrent = ''; this.cpNew = ''; this.cpConfirm = '';
    this.showCpCurrent = false; this.showCpNew = false; this.showCpConfirm = false;
    this.cpSaving = false; this.cpError = ''; this.cpSuccess = '';
    this.showCpModal = true;
  }

  closeCpModal(): void { this.showCpModal = false; }

  submitCpModal(): void {
    if (!this.cpIsValid || this.cpSaving) return;
    this.cpSaving = true;
    this.cpError = '';
    this.cpSuccess = '';
    const userId = this.auth.getCurrentUser()?.userID;
    this.api.changePassword(userId, this.cpCurrent, this.cpNew).subscribe({
      next: () => {
        this.cpSaving = false;
        this.cpSuccess = 'Password changed successfully!';
        setTimeout(() => this.closeCpModal(), 1500);
      },
      error: (err: any) => {
        this.cpSaving = false;
        this.cpError = err?.error?.message || 'Failed to change password.';
      },
    });
  }

  get currentTheme(): AppTheme { return this.themeService.currentTheme; }

  setTheme(theme: AppTheme): void {
    this.themeService.setTheme(theme);
  }

  constructor(
    private auth: AuthService,
    private centerService: CenterService,
    private router: Router,
    private pageTitleService: PageTitleService,
    public qa: QuickAccessService,
    private api: ApiService,
    private themeService: ThemeService,
    private dateFmt: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.auth.getCurrentUser();
    this.clientName = this.currentUser?.clientName || '';
    this.qa.loadFromSession();
    if (this.isManager) {
      this.managerCenters = this.centerService.getCachedCenters();
      this.sub = this.centerService.selectedCenter$.subscribe(c => {
        this.selectedCenter = c;
      });
    }
    this.titleSub = this.pageTitleService.title$.subscribe(t => {
      this.pageTitle = t;
    });
    this.picSub = this.auth.profilePic$.subscribe(pic => {
      this.profilePic = pic;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.titleSub?.unsubscribe();
    this.picSub?.unsubscribe();
  }

  get isManager(): boolean {
    return this.currentUser?.role?.toLowerCase() === 'manager';
  }

  get licenseChip(): { label: string; cls: string } | null {
    const lic = this.auth.getLicenseInfo();
    if (!lic) return null;
    const { daysRemaining, validTill } = lic;
    const dateStr = this.dateFmt.format(validTill);
    const base = `Licensed till ${dateStr}`;
    if (daysRemaining < 0)   return { label: base, cls: 'lic-critical' };
    if (daysRemaining === 0) return { label: base, cls: 'lic-critical' };
    if (daysRemaining <= 5)  return { label: base, cls: 'lic-critical' };
    if (daysRemaining <= 15) return { label: base, cls: 'lic-warning' };
    return { label: base, cls: 'lic-ok' };
  }

  qaNavigate(route: string): void { this.router.navigateByUrl(route); }

  qaIsActive(route: string): boolean {
    return this.router.url === route || this.router.url.startsWith(route + '/');
  }

  onToggleSidebar(): void { this.toggleSidebar.emit(); }

  @HostListener('document:click')
  closeMenus(): void {
    this.isUserMenuOpen = false;
    this.isCenterMenuOpen = false;
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (this.isUserMenuOpen) this.isCenterMenuOpen = false;
  }

  toggleCenterMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isCenterMenuOpen = !this.isCenterMenuOpen;
    if (this.isCenterMenuOpen) this.isUserMenuOpen = false;
  }

  selectCenter(center: any): void {
    this.centerService.setSelectedCenter(center);
    this.isCenterMenuOpen = false;
  }

  goToProfile(): void {
    this.isUserMenuOpen = false;
    this.router.navigateByUrl('/profile');
  }

  logout(): void {
    this.isUserMenuOpen = false;
    if (confirm('Are you sure you want to log out?')) {
      this.auth.logout();
    }
  }
}
